import { useMemo, useRef } from 'react';

import { UseQueryOptions } from '@lumapps/base-api/react-query';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { useStreamingQuery } from '@lumapps/streaming/hooks/useStreamingQuery';
import { getLanguageHeader } from '@lumapps/translations/ducks/selectors';
import { PartialBy } from '@lumapps/utils/types/PartialBy';

import { askAiQueryKeys, streamAskAI } from '../api';
import { ASK_AI_CHAT_STREAMING_STATUS } from '../constants';
import { AskChatParams, AskSearchBestAnswer } from '../types';

/**
 * Hook for querying the `Ask AI` streaming endpoint
 *
 * It provides `bestAnswer` which is:
 * - `{ answer: string }`: While streaming, concatenating `answerChunks`.
 * - `AskSearchBestAnswer`: The complete final answer object once the stream is `DONE`.

 * @returns The streaming query result combined with the `bestAnswer`.
 */
export const useStreamAskAi = (
    params: PartialBy<AskChatParams, 'siteIds' | 'acceptLanguageHeader' | 'conversationId'>,
    options?: UseQueryOptions,
) => {
    const currentSiteId = useSelector(instanceIdSelector);
    const acceptLanguageHeader = useSelector(getLanguageHeader);

    const chatParams: AskChatParams = {
        siteIds: [currentSiteId],
        acceptLanguageHeader,
        ...params,
    };

    const streamingQuery = useStreamingQuery({
        ...options,
        queryKey: askAiQueryKeys.chat(chatParams),
        queryFn: ({ signal }) => streamAskAI(chatParams, signal),
        /**
         * Set the stale time to infinity to only refetch this query if
         * no more observer is displayed within the page.
         */
        staleTime: options?.staleTime || Infinity,
    });

    const matchedRule = useRef<AskSearchBestAnswer['matchedRule'] | undefined>(undefined);

    /**
     * Manages the streamed answer, returning either the complete answer once the stream is marked "done"
     * or concatenating `answerChunks` as they arrive during an active stream.
     */
    const bestAnswer: AskSearchBestAnswer = useMemo(() => {
        const streamingData = streamingQuery.data;
        // Retrieve bastAnswer from the `done` chunk
        if (streamingData?.lastChunk?.data?.status === ASK_AI_CHAT_STREAMING_STATUS.DONE) {
            return streamingData?.lastChunk?.data;
        }
        // Retrieve matched rule if there's one
        if (streamingData?.lastChunk?.data?.status === ASK_AI_CHAT_STREAMING_STATUS.MATCHING_RULES) {
            matchedRule.current = streamingData?.lastChunk?.data?.matchedRule;
        }
        const concatenatedAnswer = streamingData?.chunks?.reduce((acc, curr) => {
            if (curr.data?.status === ASK_AI_CHAT_STREAMING_STATUS.STREAMING_ANSWER) {
                return `${acc}${curr.data.answerChunk}`;
            }
            return acc;
        }, '');
        return {
            answer: concatenatedAnswer,
            matchedRule: matchedRule.current,
        };
    }, [streamingQuery.data]);

    const queryWithBuiltAnswer = useMemo(
        () => ({
            ...streamingQuery,
            bestAnswer,
        }),
        [bestAnswer, streamingQuery],
    );

    return queryWithBuiltAnswer;
};
